import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, KeyRound, ArrowRight, ArrowLeft, Car, Crown, DollarSign,
  Zap, ClipboardCheck, Wrench, ShieldCheck, Rocket, TrendingDown,
  TrendingUp, Calendar, Layers, Cpu, CreditCard, CheckCircle2, ChevronDown,
  Download,
} from "lucide-react";
import { exportPitchPptx, flattenSlideForPptx } from "@/lib/exportPitchPptx";

const PIN = "2353";
const STORAGE_KEY = "priority_pitch_unlocked";

// ============ CONTENT MODEL ============
const SLIDES = [
  {
    id: 1,
    kicker: "// 01 · THE OPPORTUNITY",
    title: "A dual-brand rental car ecosystem — engineered for cash flow.",
    body:
      "Priority LLC operates a 130-car rental fleet. We are launching a two-brand direct-booking platform that captures both the luxury business-traveler market AND the value / gig / rideshare market — simultaneously, from the same fleet, powered by one unified backend and one refinanced debt structure.",
    bullets: [
      "130 vehicles → 2 storefronts → 1 backend",
      "$29–$150+ / day price band captures both ends of demand",
      "Wheelbase API + Stripe + Supabase Edge Functions = zero middlemen",
      "4-week roadmap from build to launch",
    ],
    accent: "red",
  },
  {
    id: 2,
    kicker: "// 02 · DUAL-BRAND STRATEGY",
    title: "One fleet. Two storefronts. Zero market overlap.",
    body:
      "The same physical vehicle can be tagged to serve either brand depending on demand — a calendar-lock middleware layer prevents double-booking across both sites. This lets us maximize fleet utilization without cannibalizing brand equity.",
    dual: {
      priority: {
        name: "prioritycarrentals.com",
        tag: "LUXURY · PREMIUM",
        price: "$70 – $150+ / day",
        audience: ["Business travelers", "Executive rentals", "Special event clients", "Airport VIPs"],
        vibe: "High-end direct-to-consumer digital storefront · late-model luxury assets",
      },
      advantage: {
        name: "advantagecarrentals.com",
        tag: "VALUE · REPLACEMENT",
        price: "$29 – $49 / day",
        audience: ["Gig / rideshare workers", "Local replacement transport", "Students & budget travelers", "Long-term rental cost-cutters"],
        vibe: "High-conversion, value-first digital storefront · low deposit ($250 hold)",
      },
    },
    accent: "red",
  },
  {
    id: 3,
    kicker: "// 03 · FLEET NOTATOR · IN-HOUSE OPS + EXTERNAL SAAS",
    title: "Fleet Notator: keeps <span class=\"text-[#FF3355]\">130 cars profitable</span> — and becomes a <span class=\"text-emerald-400\">SaaS revenue stream</span>.",
    body:
      "Fleet Notator is a dual-purpose product: (1) it runs Priority's in-house operations — inspections, damage evidence, maintenance, health metrics, role-based team access — and (2) it's productized for resale to other independent fleet owners and rental operators. Project lead: Justin. Same $1,000 fixed build, 2-week delivery, and every future subscription sale becomes a second monetization channel on top of the rental fleet itself.",
    features: [
      { title: "Pre / Post-Rental Inspections", desc: "Photo-verified condition reports at every rental hand-off." },
      { title: "Maintenance Management", desc: "Scheduled service tracking, history logs, and mileage triggers." },
      { title: "Damage Documentation", desc: "Photographic damage evidence chain — protects security deposits." },
      { title: "Health Metrics Dashboard", desc: "Fuel, battery, tires, fluids, engine, brakes — all in one view." },
      { title: "Role-Based Team Access", desc: "Owner · manager · inspector · mechanic — clean permissions." },
      { title: "REST API + Real-Time Reports", desc: "Operational reports and dashboards, exportable anytime." },
    ],
    roles: ["Administrator", "Fleet Manager", "Inspector", "Maintenance Technician"],
    revenueChannels: [
      { label: "In-House Ops", desc: "Runs Priority's 130-car fleet lifecycle: inspections, maintenance, damage docs, dashboards.", tone: "red" },
      { label: "External SaaS", desc: "Licensed to independent fleet owners & rental operators. Recurring monthly subscription upside.", tone: "good" },
    ],
    projectLead: { name: "Justin", role: "Project Lead · Fleet Notator" },
    sow: {
      price: "$1,000",
      priceNote: "Fixed all-in SaaS build",
      timeline: "2 weeks",
      timelineNote: "Full API + web app delivery",
      payments: [
        { pct: "50%", amount: "$500", when: "at project kickoff" },
        { pct: "50%", amount: "$500", when: "at final approval, pre-production" },
      ],
      security: ["Secure auth", "Audit logging", "Encrypted storage", "Automated backups", "Secure file uploads"],
    },
    accent: "red",
  },
  {
    id: 4,
    kicker: "// 04 · THE REFINANCE MATH",
    title: "Refinance $23k noted debt → free $102k–$126k per year in cash.",
    body:
      "The current fleet debt is being serviced as a fragmented $23,000 / month burden. Consolidating it into a single Commercial Fleet Loan or TRAC Lease (60–72 month term, blanket lien) drops monthly service to $12,500–$14,500. Every dollar freed goes directly into new vehicle acquisitions — funding fleet growth without taking on new debt.",
    numbers: [
      { label: "Current monthly debt service", value: "$23,000", tone: "bad" },
      { label: "Refinanced monthly service", value: "$12.5k – $14.5k", tone: "good" },
      { label: "Monthly cash freed", value: "$8.5k – $10.5k", tone: "good" },
      { label: "Annual cash freed", value: "$102k – $126k", tone: "good" },
      { label: "Term", value: "60 – 72 months", tone: "neutral" },
      { label: "Structure", value: "Blanket Lien · TRAC / Amortized", tone: "neutral" },
    ],
    accent: "red",
  },
  {
    id: 5,
    kicker: "// 05 · UNIT ECONOMICS",
    title: "Sub-$5k cash cars pay back in <span class=\"text-emerald-400\">9.2 months</span>. 129.6% Year-1 ROI.",
    body:
      "The Advantage brand runs on a radically different asset model: dealer-auction vehicles acquired for $3.5k–$4.5k cash, reconditioned for $500–$800, and rented at $35–$45/day. Zero debt service. ~64% net cash-flow margin per unit vs. ~41% on a financed Priority car. This is the growth engine.",
    econ: [
      {
        label: "Priority · Financed Car",
        tone: "neutral",
        rows: [
          ["Acquisition (financed)", "$25k – $35k"],
          ["All-in cash invested", "~$0 (down optional)"],
          ["Monthly debt service", "~$766"],
          ["Avg daily rate", "$75 – $110"],
          ["Gross / mo (21 days)", "~$1,785"],
          ["Net cash flow / mo", "~$739"],
          ["Net margin", "~41%"],
        ],
      },
      {
        label: "Advantage · Cash Car",
        tone: "good",
        rows: [
          ["Acquisition (auction)", "$3.5k – $4.5k"],
          ["All-in cash invested", "~$5,000"],
          ["Monthly debt service", "$0"],
          ["Avg daily rate", "$35 – $45"],
          ["Gross / mo (21 days)", "~$840"],
          ["Net cash flow / mo", "~$540"],
          ["Net margin", "~64%"],
        ],
      },
    ],
    accent: "red",
  },
  {
    id: 6,
    kicker: "// 06 · FLEET GROWTH PLAY",
    title: "Deploy $50k → add 10 cash units → offset 23% of fleet debt.",
    body:
      "Standard growth loop: $50k of capital acquires 10 Advantage cash units over 90 days. At 70% utilization ($40/day), those 10 units generate $8,400 / mo gross, $5,400 / mo net — offsetting nearly a quarter of the $23,000 / mo fleet debt overhead. Year-1 net cash from those 10 units alone: ~$64,800.",
    growth: [
      { month: "MONTH 1", units: "3 units", capex: "$15,000", cash: "Acquisition + recon", debt: "$23,000" },
      { month: "MONTH 2", units: "3 units", capex: "$15,000", cash: "Acquisition + recon", debt: "$23,000" },
      { month: "MONTH 3", units: "4 units · 10 total", capex: "$20,000", cash: "+$5,400 / mo net", debt: "$19,800 effective" },
      { month: "YEAR 1", units: "10 units live", capex: "$50,000", cash: "~$64,800 net / yr", debt: "~$38,400 offset" },
    ],
    accent: "red",
  },
  {
    id: 7,
    kicker: "// 07 · 4-WEEK ROADMAP",
    title: "Full build → launch in 30 days.",
    body: "Two phases. Two weeks each. Parallel execution across backend infrastructure, dual-brand front-ends, payment automation, and paid-media traffic acquisition.",
    phases: [
      {
        week: "WEEK 1 – 2",
        title: "Backend Foundation & Priority Site Build",
        items: [
          "Days 1–5 · Wheelbase setup + middleware API · fleet audit + tagging · refinancing fleet assembly",
          "Days 6–10 · Build prioritycarrentals.com · luxury UI · core views (Homepage, Fleet Catalog, Vehicle Detail Page)",
        ],
      },
      {
        week: "WEEK 3 – 4",
        title: "Advantage Site, Automation & Dual Launch",
        items: [
          "Days 11–15 · Build advantagecarrentals.com · value UI · discount engine",
          "Days 16–20 · Stripe two-tier payments · driver ID / contract automation · dealer auction purchasing",
          "Days 21–25 · Integration QA · cross-site calendar lock · payment / deposit test · automated comms",
          "Days 26–30 · DNS deploy · targeted ad push · lender submission",
        ],
      },
    ],
    accent: "red",
  },
  {
    id: 8,
    kicker: "// 08 · TECH STACK",
    title: "Modern infrastructure. Zero legacy tax.",
    body: "Every layer picked for speed, cost efficiency, and founder-owned code. No SaaS lock-in.",
    stack: [
      { layer: "Fleet Backbone", tech: "Wheelbase API (headless fleet + booking engine)" },
      { layer: "Middleware", tech: "Supabase Edge Functions (serverless proxy + business logic)" },
      { layer: "Frontend", tech: "React + Tailwind (dual-branded UIs, share components)" },
      { layer: "Payments", tech: "Stripe (two-tier auth: luxury full hold vs. value $250 hold)" },
      { layer: "Identity & Contracts", tech: "Stripe Identity + Wheelbase e-sign flows" },
      { layer: "Internal Ops", tech: "Fleet Notator (custom REST API + dashboard, photo-verified inspections)" },
    ],
    accent: "red",
  },
  {
    id: 9,
    kicker: "// 09 · THE ASK",
    title: "Green-light the refinance. Ship the launch in 30 days.",
    body:
      "Every day the current $23k/mo debt service continues is $283–$350 in fleet growth capital burned. Approving the refinance and greenlighting the 4-week build sequence unlocks a self-funding growth loop — where freed cash flow finances new inventory, which drives new revenue, which finances more inventory.",
    cta: [
      { label: "Approve Refinance", detail: "Single blanket lien · 60-72 months · TRAC or amortized · saves ~$9,500/mo" },
      { label: "Fund $50k Acquisition Pool", detail: "10 Advantage cash units · ~9.2 mo payback · 129.6% Year-1 ROI" },
      { label: "Greenlight 4-Week Build", detail: "Priority site · Advantage site · Fleet Notator · Automation stack" },
      { label: "Ad Push at Launch", detail: "Luxury airport / exec targeting · Value replacement / gig targeting" },
    ],
    accent: "red",
  },
];

// ============ PIN LOCK ============
function PinLock({ onUnlock }) {
  const [entered, setEntered] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);

  const handleChange = (i, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...entered];
    next[i] = val;
    setEntered(next);
    setError(false);
    if (val && i < 3) {
      document.getElementById(`pin-${i + 1}`)?.focus();
    }
    if (next.join("").length === 4) {
      if (next.join("") === PIN) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setTimeout(onUnlock, 300);
      } else {
        setError(true);
        setTimeout(() => setEntered(["", "", "", ""]), 400);
      }
    }
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !entered[i] && i > 0) {
      document.getElementById(`pin-${i - 1}`)?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center relative overflow-hidden" data-testid="priority-pin-lock">
      <div className="absolute inset-0 te-grid-bg opacity-25" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#FF3355]/15 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/12 blur-[140px]" />

      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}} className="relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-10 max-w-md w-full mx-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center justify-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-[#FF3355]/10 border border-[#FF3355]/40 flex items-center justify-center">
            <Lock size={26} className="text-[#FF3355]" />
          </div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3355] mb-3">// CONFIDENTIAL · PIN REQUIRED</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter">Priority LLC · Pitch Vault</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            This deck contains confidential financial and operational information. Enter your 4-digit access PIN to unlock.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          {entered.map((v, i) => (
            <input
              key={i}
              id={`pin-${i}`}
              data-testid={`priority-pin-${i}`}
              value={v}
              onChange={(e) => handleChange(i, e.target.value.slice(-1))}
              onKeyDown={(e) => handleKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              type="password"
              autoComplete="off"
              className={`h-16 w-14 text-center font-display text-3xl font-bold rounded-2xl bg-slate-950 border-2 outline-none transition-all ${error ? "border-red-500 animate-pulse" : v ? "border-[#FF3355] shadow-[0_0_15px_-3px_rgba(255,51,85,0.5)]" : "border-slate-800 focus:border-[#FF3355]/60"}`}
            />
          ))}
        </div>

        {error && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-red-400">
            // ACCESS DENIED · INVALID PIN
          </motion.div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          <KeyRound size={11} /> Session-locked · closes with your browser tab
        </div>
      </motion.div>
    </div>
  );
}

// ============ INFOGRAPHIC DASHBOARD ============
function Infographic() {
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80" data-testid="priority-infographic">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3355] mb-4">// AT-A-GLANCE INFOGRAPHIC</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The whole picture, <span className="text-[#FF3355]">on one screen</span>.</h2>
        </motion.div>

        {/* Top KPI row */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "130", v: "vehicle fleet", Icon: Car, color: "text-slate-100" },
            { k: "2", v: "direct-booking storefronts", Icon: Layers, color: "text-[#FF3355]" },
            { k: "$29–$150+", v: "daily price band captured", Icon: DollarSign, color: "text-emerald-400" },
            { k: "30 days", v: "build → launch", Icon: Rocket, color: "text-emerald-400" },
          ].map((s, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5,delay:i*0.06}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
              <s.Icon size={16} className={s.color} />
              <div className={`mt-3 font-display text-4xl lg:text-5xl font-bold tracking-tighter ${s.color}`}>{s.k}</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{s.v}</div>
            </motion.div>
          ))}
        </div>

        {/* Middle: Debt visual + Dual brand */}
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          {/* Debt refinance bar */}
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// DEBT REFINANCE MATH</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Cut monthly debt service by <span className="text-emerald-400">~45%</span></h3>

            <div className="mt-6 space-y-4">
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5">
                  <span className="text-red-400 flex items-center gap-1.5"><TrendingUp size={11} /> Current · fragmented</span>
                  <span className="text-slate-100 font-bold">$23,000 / mo</span>
                </div>
                <div className="h-3 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
                  <motion.div initial={{width:0}} whileInView={{width:"100%"}} viewport={{once:true}} transition={{duration:1.2,ease:"easeOut"}} className="h-full bg-gradient-to-r from-red-600 to-[#FF3355]" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5">
                  <span className="text-emerald-400 flex items-center gap-1.5"><TrendingDown size={11} /> Refinanced · single lien</span>
                  <span className="text-slate-100 font-bold">$12.5k – $14.5k / mo</span>
                </div>
                <div className="h-3 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
                  <motion.div initial={{width:0}} whileInView={{width:"57%"}} viewport={{once:true}} transition={{duration:1.4,delay:0.3,ease:"easeOut"}} className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400" />
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-emerald-500/30 bg-emerald-500/[0.05] rounded-xl p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Monthly cash freed</div>
                <div className="mt-1 font-display text-2xl font-bold text-emerald-400">$8.5k–$10.5k</div>
              </div>
              <div className="border border-emerald-500/30 bg-emerald-500/[0.05] rounded-xl p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Annual cash freed</div>
                <div className="mt-1 font-display text-2xl font-bold text-emerald-400">$102k–$126k</div>
              </div>
            </div>
          </motion.div>

          {/* Dual brand visual */}
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// DUAL-BRAND FLEET</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">One fleet. <span className="text-[#FF3355]">Two markets.</span></h3>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-[#FF3355]/40 bg-[#FF3355]/[0.05] rounded-xl p-4">
                <Crown size={16} className="text-[#FF3355]" />
                <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#FF3355]">Priority · Luxury</div>
                <div className="mt-2 font-display text-xl font-bold">$70–$150+</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">per day</div>
                <div className="mt-3 text-[11px] text-slate-300 leading-relaxed">Business travelers · execs · airport VIPs · special events</div>
              </div>
              <div className="border border-emerald-400/40 bg-emerald-500/[0.05] rounded-xl p-4">
                <Zap size={16} className="text-emerald-400" />
                <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-400">Advantage · Value</div>
                <div className="mt-2 font-display text-xl font-bold">$29–$49</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">per day</div>
                <div className="mt-3 text-[11px] text-slate-300 leading-relaxed">Gig / rideshare · replacement · students · long-term budget</div>
              </div>
            </div>

            <div className="mt-4 border border-slate-800 rounded-xl p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">SHARED BACKEND</div>
              <div className="mt-2 text-[11px] text-slate-300 leading-relaxed">
                Wheelbase API · Supabase Edge middleware · Stripe two-tier payments · cross-site calendar lock (no double-booking) · Fleet Notator ops app.
              </div>
            </div>
          </motion.div>
        </div>

        {/* Payback / ROI headline strip */}
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="mt-4 rounded-2xl p-6 bg-gradient-to-br from-emerald-500/10 via-slate-900/60 to-[#FF3355]/10 border border-emerald-500/40 backdrop-blur-xl">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6 items-center">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">// SUB-$5K CASH CAR MODEL</div>
              <h3 className="mt-2 font-display text-2xl lg:text-3xl font-bold tracking-tight">Every Advantage unit pays itself back in <span className="text-emerald-400">9.2 months</span>.</h3>
              <p className="mt-3 text-[13px] text-slate-300 leading-relaxed">Auction-sourced vehicles ($3.5k–$4.5k + $500–$800 recon) rented at $35–$45/day at 70% utilization. Zero debt service per unit. ~64% net cash-flow margin.</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">All-in / unit</div>
                <div className="mt-1 font-display text-xl font-bold text-slate-100">~$5,000</div>
              </div>
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Net cash / mo</div>
                <div className="mt-1 font-display text-xl font-bold text-emerald-400">~$540</div>
              </div>
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Payback</div>
                <div className="mt-1 font-display text-xl font-bold text-emerald-400">9.2 mo</div>
              </div>
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Year-1 ROI</div>
                <div className="mt-1 font-display text-xl font-bold text-emerald-400">129.6%</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom: 4-week timeline strip */}
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.15}} className="mt-4 bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// 30-DAY ROADMAP</div>
          <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Concept → live storefronts in <span className="text-emerald-400">4 weeks</span>.</h3>
          <div className="mt-6 grid grid-cols-4 gap-2">
            {[
              { w: "WK 1", t: "Wheelbase + API", c: "border-[#FF3355]/60 text-[#FF3355]" },
              { w: "WK 2", t: "Priority.com Build", c: "border-[#FF3355]/60 text-[#FF3355]" },
              { w: "WK 3", t: "Advantage.com + Stripe", c: "border-emerald-400/60 text-emerald-400" },
              { w: "WK 4", t: "QA + DNS + Ads", c: "border-emerald-400/60 text-emerald-400" },
            ].map((p, i) => (
              <div key={i} className={`border-2 ${p.c.split(" ")[0]} bg-slate-950/60 rounded-xl p-3 flex flex-col`}>
                <div className={`font-mono text-[9px] uppercase tracking-[0.22em] ${p.c.split(" ")[1]}`}>{p.w}</div>
                <div className="mt-1.5 font-display text-sm font-bold text-slate-100">{p.t}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ SLIDE RENDERERS ============
function SlideBody({ slide }) {
  const heading = (
    <>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FF3355] mb-4">{slide.kicker}</div>
      <h2
        className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.02]"
        dangerouslySetInnerHTML={{ __html: slide.title }}
      />
      <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed max-w-3xl">{slide.body}</p>
    </>
  );

  // Slide 1 — bullets
  if (slide.bullets) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {slide.bullets.map((b, i) => (
            <div key={i} className="te-lime-accent flex items-start gap-3 bg-slate-900/40 border border-slate-800/60 rounded-2xl p-4">
              <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-200 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Slide 2 — dual brand
  if (slide.dual) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl p-6 bg-slate-900/50 border-2 border-[#FF3355]/50 shadow-[0_0_60px_-15px_rgba(255,51,85,0.35)]">
            <Crown size={22} className="text-[#FF3355]" />
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">{slide.dual.priority.tag}</div>
            <h3 className="mt-1 font-display text-2xl font-bold">{slide.dual.priority.name}</h3>
            <div className="mt-3 font-display text-4xl font-bold text-[#FF3355]">{slide.dual.priority.price}</div>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{slide.dual.priority.vibe}</p>
            <div className="mt-5">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-2">Target audience</div>
              <ul className="space-y-1.5">{slide.dual.priority.audience.map((a) => (<li key={a} className="flex items-start gap-2 text-[13px] text-slate-200"><CheckCircle2 size={12} className="text-[#FF3355] mt-1 shrink-0" /><span>{a}</span></li>))}</ul>
            </div>
          </div>
          <div className="rounded-2xl p-6 bg-slate-900/50 border-2 border-emerald-500/50 shadow-[0_0_60px_-15px_rgba(16,185,129,0.35)]">
            <Zap size={22} className="text-emerald-400" />
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">{slide.dual.advantage.tag}</div>
            <h3 className="mt-1 font-display text-2xl font-bold">{slide.dual.advantage.name}</h3>
            <div className="mt-3 font-display text-4xl font-bold text-emerald-400">{slide.dual.advantage.price}</div>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{slide.dual.advantage.vibe}</p>
            <div className="mt-5">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-2">Target audience</div>
              <ul className="space-y-1.5">{slide.dual.advantage.audience.map((a) => (<li key={a} className="flex items-start gap-2 text-[13px] text-slate-200"><CheckCircle2 size={12} className="text-emerald-400 mt-1 shrink-0" /><span>{a}</span></li>))}</ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Slide 3 — features grid + SOW terms
  if (slide.features) {
    const icons = [ClipboardCheck, Wrench, ShieldCheck, Cpu, Layers, TrendingUp];
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {slide.features.map((f, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-2xl p-5">
                <div className="h-9 w-9 rounded-xl bg-[#FF3355]/10 border border-[#FF3355]/40 flex items-center justify-center"><Icon size={15} className="text-[#FF3355]" /></div>
                <h4 className="mt-3 font-display text-base font-bold text-slate-100">{f.title}</h4>
                <p className="mt-1.5 text-[12px] text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Revenue channels: In-House + External SaaS */}
        {slide.revenueChannels && (
          <div className="mt-6 grid md:grid-cols-2 gap-3">
            {slide.revenueChannels.map((ch, idx) => {
              const good = ch.tone === "good";
              return (
                <div key={idx} className={`rounded-2xl p-5 border-2 ${good ? "border-emerald-500/50 bg-emerald-500/[0.04]" : "border-[#FF3355]/50 bg-[#FF3355]/[0.04]"}`}>
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${good ? "bg-emerald-500/10 border border-emerald-500/50" : "bg-[#FF3355]/10 border border-[#FF3355]/50"}`}>
                      {good ? <TrendingUp size={13} className="text-emerald-400" /> : <Layers size={13} className="text-[#FF3355]" />}
                    </div>
                    <div className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold ${good ? "text-emerald-400" : "text-[#FF3355]"}`}>{ch.label}</div>
                  </div>
                  <p className="mt-3 text-[13px] text-slate-300 leading-relaxed">{ch.desc}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Project lead attribution */}
        {slide.projectLead && (
          <div className="mt-4 inline-flex items-center gap-3 bg-slate-900/50 border border-slate-800/80 rounded-full pl-1.5 pr-4 py-1.5">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#FF3355] to-emerald-400 flex items-center justify-center font-display text-sm font-bold text-slate-950">
              {slide.projectLead.name.charAt(0)}
            </div>
            <div className="text-[11px]">
              <span className="font-mono uppercase tracking-[0.22em] text-slate-400 mr-1.5">// LED BY</span>
              <span className="font-display font-bold text-slate-100">{slide.projectLead.name}</span>
              <span className="text-slate-500 mx-1.5">·</span>
              <span className="text-slate-400">{slide.projectLead.role}</span>
            </div>
          </div>
        )}

        {/* User roles chip row */}
        {slide.roles && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mr-1">// USER ROLES:</span>
            {slide.roles.map((r) => (
              <span key={r} className="inline-flex items-center gap-1.5 border border-[#FF3355]/40 bg-[#FF3355]/[0.05] text-[#FF3355] px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em]">
                <CheckCircle2 size={10} /> {r}
              </span>
            ))}
          </div>
        )}

        {/* SOW build terms */}
        {slide.sow && (
          <div className="mt-6 rounded-2xl p-6 bg-gradient-to-br from-slate-900/60 via-[#FF3355]/[0.04] to-slate-900/60 border border-[#FF3355]/40 backdrop-blur-xl" data-testid="priority-slide-sow">
            <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
              {/* Price + Timeline */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// SAAS BUILD TERMS</div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="border border-[#FF3355]/40 bg-slate-950/50 rounded-xl p-4">
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">Fixed investment</div>
                    <div className="mt-1 font-display text-3xl font-bold text-[#FF3355]">{slide.sow.price}</div>
                    <div className="mt-1 text-[11px] text-slate-500">{slide.sow.priceNote}</div>
                  </div>
                  <div className="border border-emerald-500/40 bg-slate-950/50 rounded-xl p-4">
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">Delivery</div>
                    <div className="mt-1 font-display text-3xl font-bold text-emerald-400">{slide.sow.timeline}</div>
                    <div className="mt-1 text-[11px] text-slate-500">{slide.sow.timelineNote}</div>
                  </div>
                </div>
                {slide.sow.security && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {slide.sow.security.map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 border border-emerald-500/30 bg-emerald-500/[0.04] text-emerald-400 px-2 py-0.5 rounded font-mono text-[9px] uppercase tracking-[0.18em]">
                        <ShieldCheck size={9} /> {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment schedule */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// PAYMENT SCHEDULE</div>
                <div className="mt-3 space-y-2">
                  {slide.sow.payments.map((p, i) => (
                    <div key={i} className="flex items-center gap-4 bg-slate-950/50 border border-slate-800/60 rounded-xl p-3.5">
                      <div className="h-10 w-10 rounded-lg bg-[#FF3355]/10 border border-[#FF3355]/40 flex items-center justify-center font-mono text-[11px] font-bold text-[#FF3355] shrink-0">{p.pct}</div>
                      <div className="flex-1">
                        <div className="font-display text-lg font-bold text-slate-100">{p.amount}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{p.when}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Slide 4 — refinance numbers
  if (slide.numbers) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-3">
          {slide.numbers.map((n, i) => (
            <div key={i} className={`rounded-2xl p-5 border ${n.tone === "bad" ? "border-red-900/50 bg-red-950/20" : n.tone === "good" ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-slate-800/60 bg-slate-900/50"}`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">{n.label}</div>
              <div className={`mt-2 font-display text-2xl lg:text-3xl font-bold ${n.tone === "bad" ? "text-red-400" : n.tone === "good" ? "text-emerald-400" : "text-slate-100"}`}>{n.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Slide 5 — unit economics comparison table
  if (slide.econ) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          {slide.econ.map((col, i) => {
            const good = col.tone === "good";
            return (
              <div key={i} className={`rounded-2xl p-6 border-2 ${good ? "border-emerald-500/50 bg-slate-900/50 shadow-[0_0_60px_-15px_rgba(16,185,129,0.35)]" : "border-slate-800 bg-slate-900/50"}`}>
                <div className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-3 ${good ? "text-emerald-400" : "text-slate-400"}`}>{col.label}</div>
                <div className="divide-y divide-slate-800/80">
                  {col.rows.map(([k, v], idx) => (
                    <div key={idx} className="flex items-center justify-between py-2.5 text-[13px]">
                      <span className="text-slate-400">{k}</span>
                      <span className={`font-mono font-bold ${good && idx === col.rows.length - 1 ? "text-emerald-400" : "text-slate-100"}`}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="border border-emerald-500/40 bg-emerald-500/[0.05] rounded-xl p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-400">Payback per unit</div>
            <div className="mt-1.5 font-display text-2xl font-bold text-emerald-400">9.2 months</div>
          </div>
          <div className="border border-emerald-500/40 bg-emerald-500/[0.05] rounded-xl p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-400">Year-1 ROI</div>
            <div className="mt-1.5 font-display text-2xl font-bold text-emerald-400">129.6%</div>
          </div>
          <div className="border border-slate-800/60 bg-slate-900/50 rounded-xl p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">Target utilization</div>
            <div className="mt-1.5 font-display text-2xl font-bold text-slate-100">70%</div>
          </div>
          <div className="border border-slate-800/60 bg-slate-900/50 rounded-xl p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">Target mileage band</div>
            <div className="mt-1.5 font-display text-2xl font-bold text-slate-100">110k – 145k</div>
          </div>
        </div>
      </div>
    );
  }

  // Slide 6 — fleet growth timeline
  if (slide.growth) {
    return (
      <div>
        {heading}
        <div className="mt-8 relative">
          <div className="hidden lg:block absolute top-6 left-6 right-6 h-px bg-gradient-to-r from-[#FF3355] via-emerald-500 to-emerald-400" aria-hidden />
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            {slide.growth.map((g, i) => {
              const isYear = g.month.startsWith("YEAR");
              return (
                <div key={i} className={`relative bg-slate-900/50 border-2 rounded-2xl p-5 ${isYear ? "border-emerald-500/60 shadow-[0_0_40px_-15px_rgba(16,185,129,0.4)]" : "border-slate-800/60"}`}>
                  <div className={`hidden lg:block absolute -top-1.5 left-6 h-3 w-3 rounded-full ${isYear ? "bg-emerald-400" : "bg-[#FF3355]"}`} />
                  <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${isYear ? "text-emerald-400" : "text-[#FF3355]"}`}>{g.month}</div>
                  <div className="mt-3 font-display text-xl font-bold text-slate-100">{g.units}</div>
                  <div className="mt-3 space-y-2 text-[12px]">
                    <div className="flex justify-between gap-2"><span className="text-slate-500 font-mono uppercase tracking-[0.18em] text-[9px]">Capex</span><span className="text-slate-200 font-mono font-bold">{g.capex}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-slate-500 font-mono uppercase tracking-[0.18em] text-[9px]">Cash</span><span className={`font-mono font-bold ${isYear ? "text-emerald-400" : "text-slate-200"}`}>{g.cash}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-slate-500 font-mono uppercase tracking-[0.18em] text-[9px]">Debt</span><span className={`font-mono font-bold ${g.debt.includes("effective") || g.debt.includes("offset") ? "text-emerald-400" : "text-slate-400"}`}>{g.debt}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-6 border border-emerald-500/30 bg-emerald-500/[0.04] rounded-2xl p-5 flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center shrink-0"><CheckCircle2 size={18} className="text-emerald-400" /></div>
          <div className="text-sm text-slate-200 leading-relaxed">
            <span className="text-emerald-400 font-semibold">Standard growth loop:</span> every $50k of freed cash from the refinance funds another 10-unit tranche. Each tranche self-amortizes in 9.2 months and generates ~$64,800 / year net thereafter. Repeatable indefinitely.
          </div>
        </div>
      </div>
    );
  }

  // Slide — phases
  if (slide.phases) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          {slide.phases.map((p, i) => (
            <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-xl bg-[#FF3355]/10 border border-[#FF3355]/40 flex items-center justify-center"><Calendar size={15} className="text-[#FF3355]" /></div>
                <div><div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">{p.week}</div><div className="font-display text-lg font-bold">{p.title}</div></div>
              </div>
              <ul className="space-y-2">{p.items.map((it, j) => (<li key={j} className="flex items-start gap-2 text-[13px] text-slate-300 leading-relaxed"><ArrowRight size={12} className="text-[#FF3355] mt-1 shrink-0" /><span>{it}</span></li>))}</ul>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Slide 6 — tech stack
  if (slide.stack) {
    const icons = [Cpu, Layers, Zap, CreditCard, ShieldCheck, ClipboardCheck];
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-2 gap-3">
          {slide.stack.map((s, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-2xl p-5 flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-[#FF3355]/10 border border-[#FF3355]/40 flex items-center justify-center shrink-0"><Icon size={16} className="text-[#FF3355]" /></div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">{s.layer}</div>
                  <div className="mt-1 text-sm text-slate-200 leading-relaxed">{s.tech}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Slide 7 — CTA
  if (slide.cta) {
    return (
      <div>
        {heading}
        <div className="mt-8 space-y-3">
          {slide.cta.map((c, i) => (
            <div key={i} className="bg-slate-900/50 border border-[#FF3355]/40 hover:border-[#FF3355] transition-colors rounded-2xl p-5 flex items-center justify-between gap-5">
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#FF3355]/10 border border-[#FF3355]/50 flex items-center justify-center shrink-0"><CheckCircle2 size={16} className="text-[#FF3355]" /></div>
                <div><div className="font-display text-lg font-bold text-slate-100">{c.label}</div><div className="text-[12px] text-slate-400 mt-0.5">{c.detail}</div></div>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 hidden sm:block">GO</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// ============ DECK ============
function Deck() {
  const [i, setI] = useState(0);
  const [printing, setPrinting] = useState(false);
  const next = useCallback(() => setI((v) => Math.min(SLIDES.length - 1, v + 1)), []);
  const prev = useCallback(() => setI((v) => Math.max(0, v - 1)), []);
  const slide = SLIDES[i];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    const beforePrint = () => setPrinting(true);
    const afterPrint = () => setPrinting(false);
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, [next, prev]);

  return (
    <section id="deck" className="relative py-20 lg:py-28 scroll-mt-24" data-testid="priority-deck">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3355] mb-4">// FULL PITCH DECK · {SLIDES.length} SLIDES</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The deck.</h2>
          <p className="mt-3 text-slate-400 text-sm">Use ← → arrow keys or click the pager below to navigate. Slide {i + 1} of {SLIDES.length}.</p>
        </motion.div>

        {printing ? (
          // Print mode: render ALL slides sequentially, one per page
          <div className="mt-8 space-y-6">
            {SLIDES.map((s) => (
              <div key={s.id} className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:p-12" data-testid={`priority-slide-${s.id}`}>
                <SlideBody slide={s} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="mt-8 relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-6 lg:p-12 min-h-[560px] overflow-hidden">
              <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-[#FF3355]/10 blur-[120px]" aria-hidden />
              <AnimatePresence mode="wait">
                <motion.div
                  key={slide.id}
                  initial={{opacity:0, x:30}}
                  animate={{opacity:1, x:0}}
                  exit={{opacity:0, x:-30}}
                  transition={{duration:0.35, ease:[0.22,1,0.36,1]}}
                  className="relative"
                  data-testid={`priority-slide-${slide.id}`}
                >
                  <SlideBody slide={slide} />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Pager */}
            <div className="mt-6 flex items-center justify-between pitch-no-print">
              <button
                onClick={prev}
                disabled={i === 0}
                data-testid="priority-deck-prev"
                className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ArrowLeft size={13} /> Prev
              </button>
              <div className="flex items-center gap-1.5">
                {SLIDES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setI(idx)}
                    data-testid={`priority-deck-dot-${idx}`}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-[#FF3355]" : "w-2 bg-slate-700 hover:bg-slate-500"}`}
                  />
                ))}
              </div>
              <button
                onClick={next}
                disabled={i === SLIDES.length - 1}
                data-testid="priority-deck-next"
                className="inline-flex items-center gap-2 bg-[#FF3355] text-white px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#ff4d6a] disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-[0_0_20px_-6px_rgba(255,51,85,0.6)]"
              >
                Next <ArrowRight size={13} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ============ EXECUTIVE SUMMARY ============
function ExecutiveSummary() {
  return (
    <section className="relative overflow-hidden border-b border-slate-800/80" data-testid="priority-summary">
      <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#FF3355]/15 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/12 blur-[140px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20">
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-500/[0.06] text-emerald-400 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]">
          <ShieldCheck size={12} /> UNLOCKED · Priority LLC · Confidential
        </motion.div>

        <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9,delay:0.1,ease:[0.22,1,0.36,1]}} className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.98]">
            Priority LLC · <span className="text-[#FF3355]">Dual-Brand Rental Fleet</span> Pitch Vault.
        </motion.h1>

        <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8,delay:0.25}} className="mt-6 text-base sm:text-lg text-slate-300 max-w-4xl leading-relaxed">
          Executive summary of the go-to-market plan for a 130-vehicle rental fleet split across two branded direct-booking storefronts (<span className="text-[#FF3355] font-semibold">prioritycarrentals.com</span> for luxury, <span className="text-emerald-400 font-semibold">advantagecarrentals.com</span> for value), backed by an internal ops app (Fleet Notator), a refinanced debt structure that frees <span className="text-emerald-400 font-semibold">$102k–$126k / year</span> in cash flow, and a sub-$5k cash-acquisition model with a <span className="text-emerald-400 font-semibold">9.2-month payback</span> and <span className="text-emerald-400 font-semibold">129.6% Year-1 ROI</span>.
        </motion.p>

        {/* TL;DR grid */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.3}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// THE STRATEGY</div>
            <h3 className="mt-2 font-display text-lg font-bold">Dual-brand, one backend</h3>
            <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">130-car fleet serving luxury travelers ($70–$150+/day) and gig / replacement drivers ($29–$49/day) simultaneously without brand cannibalization.</p>
          </motion.div>
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.4}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// THE MATH</div>
            <h3 className="mt-2 font-display text-lg font-bold">Refinance $23k → $12.5k / mo</h3>
            <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">Consolidate fragmented debt into a single 60–72 month blanket lien. Frees <span className="text-emerald-400 font-semibold">$8.5k–$10.5k / mo</span> for new vehicle acquisitions.</p>
          </motion.div>
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.45}} className="bg-slate-900/60 border border-emerald-500/40 bg-emerald-500/[0.03] backdrop-blur-xl rounded-2xl p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">// THE ROI</div>
            <h3 className="mt-2 font-display text-lg font-bold">Sub-$5k car · 9.2mo payback</h3>
            <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">Advantage cash cars deliver <span className="text-emerald-400 font-semibold">129.6% Year-1 ROI</span> at 70% utilization. ~64% net margin per unit vs. 41% financed.</p>
          </motion.div>
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.5}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">// THE TIMELINE</div>
            <h3 className="mt-2 font-display text-lg font-bold">30 days · Build → Launch</h3>
            <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">2 phases · 4 weeks · Wheelbase API + Stripe + Supabase Edge + Fleet Notator. DNS-live sites with paid ads on day 30.</p>
          </motion.div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="#deck" data-testid="priority-cta-view-deck" className="inline-flex items-center gap-2 bg-[#FF3355] text-white px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#ff4d6a] hover:scale-[1.02] transition-all shadow-[0_0_30px_-8px_rgba(255,51,85,0.6)]">
            <ArrowRight size={13} /> View Full Deck
          </a>
          <a href="#deck" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); }} className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 transition-all">
            <ChevronDown size={13} /> Scroll Through
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ MAIN PAGE ============
export default function PriorityPitch() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
  }, []);

  const title = useMemo(() => "Priority LLC · Pitch Vault", []);
  useEffect(() => {
    document.title = title;
    return () => { document.title = "TraceElements · We architect intelligent infrastructure."; };
  }, [title]);

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="priority-pitch-page">
      <ExecutiveSummary />
      <Infographic />
      <Deck />

      {/* Footer strip */}
      <section className="border-t border-slate-800/80 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 pitch-no-print">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            // CONFIDENTIAL · Priority LLC · Prepared by TraceElements
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="priority-download-pdf"
              className="inline-flex items-center gap-2 bg-[#FF3355] text-white hover:bg-[#ff4d6a] px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all shadow-[0_0_20px_-6px_rgba(255,51,85,0.6)]"
            >
              <Download size={12} /> Download PDF
            </button>
            <button
              onClick={() => exportPitchPptx({
                vaultName: "Priority LLC",
                title: "Priority LLC · Dual-Brand Rental Fleet Pitch Vault",
                subtitle: "Vertically integrated rental fleet — TraceElements confidential investor deck.",
                brand: "FF3355",
                slides: SLIDES.map((s) => ({ kicker: s.kicker, title: s.title, body: s.body, bullets: flattenSlideForPptx(s) })),
              })}
              data-testid="priority-download-pptx"
              className="inline-flex items-center gap-2 border border-[#FF3355]/60 bg-[#FF3355]/10 text-[#FF3355] hover:bg-[#FF3355]/20 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all"
            >
              <Download size={12} /> Download PPT
            </button>
            <button
              onClick={() => { sessionStorage.removeItem(STORAGE_KEY); window.location.reload(); }}
              data-testid="priority-relock"
              className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] transition-all"
            >
              <Lock size={11} /> Relock Session
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
